import { useState } from 'react';

import { Field, Modal, Notice } from '@/components/Modal';
import { EmptyCard, LoadingBlock, PageHeader } from '@/components/PageHeader';

import { useHeatZones, useHeatZoneWrite, type HeatZone, type HeatZoneInput } from './heatApi';

const BLANK: HeatZoneInput = {
  name: '',
  outdoor: true,
  hasShade: false,
  hasRestArea: false,
  waterSpot: null,
  cooling: null,
  note: null,
  active: true,
};

/** 현장 › 온열질환 구역 및 환경 설정 — 작업 구역마다 실내 · 실외, 그늘막 · 휴게시설, 음용수, 냉방장비(V575). */
export function SiteHeatZonesPage({ siteId }: { siteId: string }) {
  const zones = useHeatZones(siteId);
  const [editing, setEditing] = useState<{ id: string | null; value: HeatZoneInput } | null>(null);
  const rows = zones.data ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title="구역 및 환경 설정"
        actions={
          <button type="button" className="btn-primary" onClick={() => setEditing({ id: null, value: { ...BLANK } })}>
            구역 추가
          </button>
        }
      />
      {zones.isPending ? (
        <LoadingBlock />
      ) : zones.isError ? (
        <LoadingBlock />
      ) : rows.length === 0 ? (
        <EmptyCard>등록된 구역이 없습니다.</EmptyCard>
      ) : (
        <div className="card p-0" style={{ overflowX: 'auto' }}>
          <table className="site-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>구역명</th>
                <th style={{ width: '72px' }}>구분</th>
                <th style={{ width: '72px' }}>그늘막</th>
                <th style={{ width: '80px' }}>휴게시설</th>
                <th style={{ textAlign: 'left' }}>음용수 위치</th>
                <th style={{ textAlign: 'left' }}>냉방장비</th>
                <th style={{ textAlign: 'left' }}>비고</th>
                <th style={{ width: '72px' }}>사용</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((zone) => (
                <tr key={zone.id} onClick={() => setEditing({ id: zone.id, value: toInput(zone) })} style={{ cursor: 'pointer' }}>
                  <td style={{ textAlign: 'left', fontWeight: 600 }}>{zone.name}</td>
                  <td>{zone.outdoor ? '실외' : '실내'}</td>
                  <td>{zone.hasShade ? '있음' : '—'}</td>
                  <td>{zone.hasRestArea ? '있음' : '—'}</td>
                  <td style={{ textAlign: 'left' }}>{zone.waterSpot ?? '—'}</td>
                  <td style={{ textAlign: 'left' }}>{zone.cooling ?? '—'}</td>
                  <td style={{ textAlign: 'left', whiteSpace: 'pre-wrap' }}>{zone.note ?? '—'}</td>
                  <td>
                    <span className={`badge ${zone.active ? 'badge-green' : 'badge-gray'}`}>{zone.active ? '사용' : '중지'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <ZoneModal siteId={siteId} id={editing.id} initial={editing.value} onClose={() => setEditing(null)} />}
    </div>
  );
}

function toInput(zone: HeatZone): HeatZoneInput {
  return {
    name: zone.name,
    outdoor: zone.outdoor,
    hasShade: zone.hasShade,
    hasRestArea: zone.hasRestArea,
    waterSpot: zone.waterSpot,
    cooling: zone.cooling,
    note: zone.note,
    active: zone.active,
  };
}

function ZoneModal({
  siteId,
  id,
  initial,
  onClose,
}: {
  siteId: string;
  id: string | null;
  initial: HeatZoneInput;
  onClose: () => void;
}) {
  const write = useHeatZoneWrite(siteId);
  const [value, setValue] = useState<HeatZoneInput>(initial);
  const set = <K extends keyof HeatZoneInput>(key: K, next: HeatZoneInput[K]) => setValue((prev) => ({ ...prev, [key]: next }));
  const text = (key: 'waterSpot' | 'cooling' | 'note') => value[key] ?? '';

  const save = () => write.mutate({ id, body: value }, { onSuccess: onClose });
  const remove = () => {
    if (!id || !window.confirm(`「${initial.name}」 구역을 지울까요?`)) return;
    write.mutate({ id, body: null }, { onSuccess: onClose });
  };

  return (
    <Modal
      title={id ? '구역 수정' : '구역 추가'}
      onClose={onClose}
      closeDisabled={write.isPending}
      maxWidth="520px"
      actions={
        <>
          {id && (
            <button type="button" className="btn-secondary" onClick={remove} disabled={write.isPending}>
              삭제
            </button>
          )}
          <button type="button" className="btn-primary" onClick={save} disabled={write.isPending}>
            저장
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {write.isError && <Notice tone="error">{write.error instanceof Error ? write.error.message : '저장하지 못했습니다.'}</Notice>}
        <Field label="구역명">
          <input className="input" value={value.name} onChange={(e) => set('name', e.target.value)} maxLength={100} />
        </Field>
        <Field label="구분">
          <select className="input" value={value.outdoor ? 'OUT' : 'IN'} onChange={(e) => set('outdoor', e.target.value === 'OUT')}>
            <option value="OUT">실외</option>
            <option value="IN">실내</option>
          </select>
        </Field>
        <div className="flex flex-wrap gap-5 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={value.hasShade} onChange={(e) => set('hasShade', e.target.checked)} />
            그늘막
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={value.hasRestArea} onChange={(e) => set('hasRestArea', e.target.checked)} />
            휴게시설
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={value.active} onChange={(e) => set('active', e.target.checked)} />
            사용
          </label>
        </div>
        <Field label="음용수 위치">
          <input className="input" value={text('waterSpot')} onChange={(e) => set('waterSpot', e.target.value)} maxLength={200} />
        </Field>
        <Field label="냉방장비">
          <input className="input" value={text('cooling')} onChange={(e) => set('cooling', e.target.value)} maxLength={200} />
        </Field>
        <Field label="비고">
          <textarea className="input" rows={3} value={text('note')} onChange={(e) => set('note', e.target.value)} maxLength={1000} />
        </Field>
      </div>
    </Modal>
  );
}
